/**
 * Fehlerantwort einer Modul-API im Format RFC 7807 (ProblemDetails).
 * So liefern es ASP.NET-Backends standardmäßig aus.
 */
export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  /** Feldbezogene Validierungsfehler: Feldname → Meldungen. */
  errors?: Record<string, string[]>
  [key: string]: unknown
}

/**
 * Fehler eines API-Aufrufs. Jeder fehlgeschlagene Aufruf über
 * `useApiClient()` wirft genau diesen Typ — Module brauchen keine eigene
 * Fehlerauswertung.
 *
 * @example
 * ```ts
 * try {
 *   await api.post('/vorgaenge', werte)
 * } catch (error) {
 *   if (error instanceof ApiError && error.status === 409) {
 *     toast.error('Der Vorgang wurde zwischenzeitlich geändert.')
 *   } else {
 *     throw error
 *   }
 * }
 * ```
 */
export class ApiError extends Error {
  /** HTTP-Statuscode. 0, wenn die Anfrage das Netz nicht verlassen hat. */
  readonly status: number
  /** Kurzform des Fehlers, für die Anzeige geeignet. */
  readonly title: string
  /** Ausführliche Beschreibung, sofern das Backend eine liefert. */
  readonly detail: string | undefined
  /** Kennung dieser Anfrage — beim Support angeben. */
  readonly traceId: string
  /** Feldbezogene Validierungsfehler, falls vorhanden. */
  readonly fieldErrors: Record<string, string[]> | undefined
  /** Die vollständige Antwort des Backends. */
  readonly problem: ProblemDetails | undefined

  constructor(init: {
    status: number
    title: string
    detail?: string | undefined
    traceId: string
    problem?: ProblemDetails | undefined
  }) {
    super(init.detail ? `${init.title}: ${init.detail}` : init.title)
    this.name = 'ApiError'
    this.status = init.status
    this.title = init.title
    this.detail = init.detail
    this.traceId = init.traceId
    this.problem = init.problem
    this.fieldErrors = init.problem?.errors
  }

  /** Der Nutzer ist nicht angemeldet oder die Sitzung ist abgelaufen. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** Der Nutzer ist angemeldet, darf das aber nicht. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /** Es gibt nichts unter dieser Adresse. */
  get isNotFound(): boolean {
    return this.status === 404
  }

  /**
   * Text, den man einem Nutzer zeigen kann — ohne technische Begriffe.
   *
   * @example
   * ```tsx
   * <ErrorState description={error.userMessage()} details={error.traceId} />
   * ```
   */
  userMessage(): string {
    if (this.status === 0) {
      return 'Die Verbindung zum Server ist fehlgeschlagen. Prüfen Sie Ihre Netzwerkverbindung.'
    }
    if (this.isUnauthorized) {
      return 'Ihre Anmeldung ist abgelaufen. Melden Sie sich erneut an.'
    }
    if (this.isForbidden) {
      return 'Für diesen Vorgang fehlt Ihnen die Berechtigung.'
    }
    if (this.isNotFound) {
      return 'Der angeforderte Eintrag existiert nicht mehr.'
    }
    if (this.status >= 500) {
      return 'Der Server hat die Anfrage nicht verarbeiten können. Versuchen Sie es später erneut.'
    }
    return this.detail ?? this.title
  }
}
